// src/components/AddStationModal.jsx
import { useState, useEffect } from "react";

// UI-Komponenten
import Modal from "./ui/Modal";
import Input from "./ui/Input";
import Textarea from "./ui/Textarea";
import Select from "./ui/Select";
import Button from "./ui/Button";
import Spinner from "./ui/Spinner";
import Skeleton from "./ui/Skeleton";
import ZeitmessungFelder, { zeitmessungPayload } from "./ZeitmessungFelder";
import { SummenrechnerSchalter } from "./Summenrechner";
import QuizFelder, { QUIZ_STANDARD, quizPayload } from "./QuizFelder";
import SymbolAuswahl from "./SymbolAuswahl";
import SponsorLogoFeld, { useSponsorLogo, logoSpeichern } from "./SponsorLogoFeld";
import { iconVorschlag } from "../utils/stationIcons";
import { mitSitzungspruefung } from "../utils/fetchWithAuth";

export default function AddStationModal({ open, onClose, onSaved }) {
  const [stationName, setStationName] = useState("");
  const [stationNumber, setStationNumber] = useState("");
  const [supervisorName, setSupervisorName] = useState("");
  const [gameDescription, setGameDescription] = useState("");
  const [rules, setRules] = useState("");
  const [scoreLogic, setScoreLogic] = useState("");
  const [materials, setMaterials] = useState("");
  const [funFact, setFunFact] = useState("");
  // null = Symbol folgt automatisch dem Stationsnamen; sobald jemand eines
  // antippt, bleibt es so.
  const [iconGewaehlt, setIconGewaehlt] = useState(null);
  const [scoreDirection, setScoreDirection] = useState("higher");
  const [unit, setUnit] = useState("");
  const [stationType, setStationType] = useState("normal");
  const [correctValue, setCorrectValue] = useState("");
  const [zeitmessung, setZeitmessung] = useState("off");
  const [countdownSeconds, setCountdownSeconds] = useState("");
  const [stopwatchMaxSeconds, setStopwatchMaxSeconds] = useState("");
  const [stopwatchAttempts, setStopwatchAttempts] = useState("");
  const [personenSumme, setPersonenSumme] = useState(false);
  const [quizWerte, setQuizWerte] = useState({ ...QUIZ_STANDARD });
  const [allowGroups, setAllowGroups] = useState("Alle");
  const [sponsor, setSponsor] = useState("");
  const [bereich, setBereich] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Reset bei Öffnen
  useEffect(() => {
    if (open) {
      setStationName("");
      setSupervisorName("");
      setGameDescription("");
      setRules("");
      setScoreLogic("");
      setMaterials("");
      setFunFact("");
      setIconGewaehlt(null);
      setScoreDirection("higher");
      setUnit("");
      setStationType("normal");
      setCorrectValue("");
      setZeitmessung("off");
      setCountdownSeconds("");
      setStopwatchMaxSeconds("");
      setStopwatchAttempts("");
      setPersonenSumme(false);
      setQuizWerte({ ...QUIZ_STANDARD });
      // BEHOBENER FEHLER: stand auf `true` (Boolean). Jede neue Station, bei
      // der "Erlaubt für" nicht angefasst wurde, bekam allow_groups: true.
      setAllowGroups("Alle");
      setError("");
      setSponsor("");
      setBereich("");
    }
  }, [open]);

  // Beim Anlegen gibt es noch kein gespeichertes Logo – nur eine Auswahl, die
  // nach dem Anlegen der Station hochgeladen wird.
  const logo = useSponsorLogo(null, false, open);

  // Nächste Stationsnummer laden
  useEffect(() => {
    if (open) {
      const fetchStations = async () => {
        setIsLoading(true);
        setError("");
        try {
          const res = mitSitzungspruefung(await fetch(`${BACKEND_URL}/stations`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }));
          if (res.ok) {
            const stations = await res.json();
            const maxNum =
              stations.length > 0
                ? Math.max(...stations.map((s) => s.station_number))
                : 0;
            setStationNumber(maxNum + 1);
          } else {
            setError("Fehler beim Laden der Stationsnummern");
          }
        } catch (err) {
          console.error("Fehler beim Laden der Stationen:", err);
          setError("Netzwerkfehler beim Laden der Stationen");
        } finally {
          setIsLoading(false);
        }
      };
      fetchStations();
    }
  }, [open, BACKEND_URL]);

  const iconName =
    iconGewaehlt ?? iconVorschlag(stationName, { schaetzstation: stationType === "estimate" });

  const canSave = () =>
    stationName.trim() && stationNumber && !isSaving && !isLoading;

  const handleSave = async () => {
    if (!canSave()) {
      setError("Stationsname muss ausgefüllt sein.");
      return;
    }

    setIsSaving(true);
    setError("");

    const payload = {
      station_name: stationName.trim(),
      station_number: parseInt(stationNumber),
      supervisor_name: supervisorName.trim(),
      sponsor: sponsor.trim(),
      bereich: bereich.trim(),
      game_description: gameDescription.trim(),
      rules: rules.trim(),
      score_logic: scoreLogic.trim(),
      materials: materials.trim(),
      fun_fact: funFact.trim(),
      icon_name: iconName,
      score_direction: scoreDirection,
      station_type: stationType,
      correct_value: stationType === "estimate" ? Number(correctValue) : null,
      // Zeitmessung: Countdown ODER Stoppuhr (+ Maximalzeit); bei
      // Schaetzstationen gibt es nichts zu messen.
      ...zeitmessungPayload({
        art: zeitmessung,
        countdownSeconds,
        maxSeconds: stopwatchMaxSeconds,
        versuche: stopwatchAttempts,
        // Quiz: die Uhr laeuft am Tablet, im Ergebnisdialog gibt es nichts zu messen.
        istSchaetzstation: stationType !== "normal",
      }),
      ...quizPayload(quizWerte, stationType === "quiz"),
      person_sum: stationType === "normal" && personenSumme,
      allow_groups: allowGroups,
      unit: unit.trim(),
      results_count_kids: 0,
      results_count_adults: 0,
      results: [],
    };

    try {
      const res = mitSitzungspruefung(await fetch(`${BACKEND_URL}/stations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      }));

      if (res.ok) {
        const created = await res.json(); // Backend gibt { station, results } zurück
        try {
          await logoSpeichern(created.station.station_number, logo.aenderung);
        } catch (err) {
          // Station steht – nur das Logo fehlt. Dialog offen lassen waere
          // irrefuehrend (erneutes Speichern: "existiert bereits").
          window.alert(
            "Die Station wurde angelegt, das Logo aber nicht gespeichert: " +
              (err.message || "Fehler") +
              ". Bitte unter „Bearbeiten“ erneut hochladen."
          );
        }
        onSaved?.(created.station);       // ⚡ neue Station zurückgeben
        onClose?.();
      } else {
        const err = await res.json();
        setError(err.error || "Fehler beim Speichern der Station");
      }
    } catch (err) {
      console.error(err);
      setError("Netzwerkfehler beim Speichern der Station");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Neue Station hinzufügen"
      className="max-w-3xl"  // Desktop breiter
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSaving || isLoading}
          >
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!canSave()}
          >
            {isSaving ? (
              <>
                <Spinner size={4} /> Speichern…
              </>
            ) : (
              "Speichern"
            )}
          </Button>
        </>
      }
    >
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <div className="grid gap-4">
          <Input
            label="Stationsname"
            value={stationName}
            onChange={(e) => setStationName(e.target.value)}
            disabled={isSaving}
          />

          <Input
            label="Stationsnummer"
            type="number"
            value={stationNumber}
            onChange={(e) => setStationNumber(e.target.value)}
            disabled={isSaving}
          />

          <Input
            label="Betreuer"
            value={supervisorName}
            onChange={(e) => setSupervisorName(e.target.value)}
            disabled={isSaving}
          />

          <Input
            label="Sponsor"
            value={sponsor}
            onChange={(e) => setSponsor(e.target.value)}
            disabled={isSaving}
          />

          <SponsorLogoFeld logo={logo} disabled={isSaving} />

          {/* Flaeche auf dem Festgelaende – gliedert die Spielliste auf dem
              Team-Zettel. Fuer alle Stationen auf einmal geht es unter
              „Lageplan“ bequemer. */}
          <Input
            label="Bereich (z. B. Halle, Parkplatz Nord)"
            value={bereich}
            onChange={(e) => setBereich(e.target.value)}
            maxLength={40}
            disabled={isSaving}
          />

          {/* ➜ Mehrzeilige/auto-wachsende Felder */}
          <Textarea
            label="Spielbeschreibung"
            value={gameDescription}
            onChange={(e) => setGameDescription(e.target.value)}
            disabled={isSaving}
            rows={3}
          />

          <Textarea
            label="Regeln"
            value={rules}
            onChange={(e) => setRules(e.target.value)}
            disabled={isSaving}
            rows={3}
          />

          <Textarea
            label="Bewertung / Punkte-Logik"
            value={scoreLogic}
            onChange={(e) => setScoreLogic(e.target.value)}
            disabled={isSaving}
            rows={3}
          />

          <Input
            label="Materialien"
            value={materials}
            onChange={(e) => setMaterials(e.target.value)}
            disabled={isSaving}
          />

          <Textarea
            label="Fun Fact"
            value={funFact}
            onChange={(e) => setFunFact(e.target.value)}
            disabled={isSaving}
            rows={3}
          />

          <SymbolAuswahl
            value={iconName}
            onChange={setIconGewaehlt}
            disabled={isSaving}
            hinweis={iconGewaehlt ? "" : "Automatisch passend zum Namen gewählt – zum Ändern einfach ein anderes antippen."}
          />

          <Input
            label="Einheit"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            disabled={isSaving}
          />

          <Select
            label="Bewertungsrichtung"
            value={scoreDirection}
            onChange={(e) => setScoreDirection(e.target.value)}
            options={[
              { value: "higher", label: "Höher ist besser" },
              { value: "lower", label: "Niedriger ist besser" },
            ]}
            disabled={isSaving || stationType === "quiz"}
          />

          <Select
            label="Stations-Typ"
            value={stationType}
            onChange={(e) => {
              const value = e.target.value;
              setStationType(value);
              if (value === "estimate") setScoreDirection("lower");
              if (value === "quiz") setScoreDirection("higher");
            }}
            options={[
              { value: "normal", label: "Normale Station" },
              { value: "estimate", label: "Schätzstation" },
              { value: "quiz", label: "Quiz am Tablet (Wahr/Falsch, Mehrfachauswahl)" },
            ]}
            disabled={isSaving}
          />

          {stationType === "estimate" && (
            <Input
              type="number"
              label="Korrektwert"
              value={correctValue}
              onChange={(e) => setCorrectValue(e.target.value)}
              disabled={isSaving}
            />
          )}

          {/* Quiz: Spielzeiten und Regeln; Fragen und PIN unter „Quiz“. */}
          {stationType === "quiz" && (
            <QuizFelder werte={quizWerte} onChange={setQuizWerte} disabled={isSaving} />
          )}

          {/* Optionale Zeitmessung (Countdown oder Stoppuhr) fuer Zeitspiele. Sie
              erscheint im Ergebnisdialog; direkte Eingabe bleibt moeglich. */}
          {stationType === "normal" && (
            <ZeitmessungFelder
              art={zeitmessung}
              onArt={setZeitmessung}
              countdownSeconds={countdownSeconds}
              onCountdown={setCountdownSeconds}
              maxSeconds={stopwatchMaxSeconds}
              onMax={setStopwatchMaxSeconds}
              versuche={stopwatchAttempts}
              onVersuche={setStopwatchAttempts}
              disabled={isSaving}
            />
          )}

          {stationType === "normal" && (
            <SummenrechnerSchalter
              checked={personenSumme}
              onChange={setPersonenSumme}
              disabled={isSaving}
            />
          )}

          <Select
            label="Erlaubt für"
            value={allowGroups}
            onChange={(e) => setAllowGroups(e.target.value)}
            options={[
              { value: "Alle", label: "Alle (Kinder & Erwachsene)" },
              { value: "Erwachsene", label: "Nur Erwachsene" },
              { value: "Kinder", label: "Nur Kinder" },
            ]}
            disabled={isSaving}
          />
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-800 text-red-200 rounded">
          {error}
        </div>
      )}
    </Modal>
  );
}
